import { useEffect, useRef, useState } from "react";

export interface NavItem {
  key: string;
  label: string;
  icon: string;
}

/**
 * Sticky horizontal category rail with scroll-spy: highlights the section in view
 * (IntersectionObserver), auto-centers the active pill, and jump-scrolls on tap.
 * Sections must have id `sec-{key}` and enough scroll-margin-top to clear the bars.
 */
export function CategoryNav({ items }: { items: NavItem[] }) {
  const [active, setActive] = useState(items[0]?.key ?? "");
  const railRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id.replace("sec-", ""));
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );
    items.forEach((it) => {
      const el = document.getElementById(`sec-${it.key}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [items]);

  useEffect(() => {
    const pill = railRef.current?.querySelector(`[data-key="${active}"]`);
    pill?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [active]);

  function go(key: string) {
    document.getElementById(`sec-${key}`)?.scrollIntoView({ behavior: "smooth" });
    setActive(key);
  }

  return (
    <div className="sticky top-14 z-30 -mx-4 border-b border-black/5 bg-cream/85 px-4 py-2 backdrop-blur">
      <div ref={railRef} className="no-scrollbar flex gap-2 overflow-x-auto">
        {items.map((it) => (
          <button
            key={it.key}
            data-key={it.key}
            onClick={() => go(it.key)}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition ${
              active === it.key
                ? "bg-brand text-cream"
                : "bg-black/5 text-black/60 hover:bg-black/10"
            }`}
          >
            <span>{it.icon}</span>
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}
